import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import type { Document } from '@contentful/rich-text-types';
import Link from 'next/link';
import Image from 'next/image';
import CursorTooltip from './CursorTooltip';
// Using plain img for embedded assets to avoid hydration/removeChild issues

type ContentRendererProps = {
  content: Document;
};

// Define custom rendering options for rich text
const renderOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => <strong className="font-semibold">{text}</strong>,
    [MARKS.ITALIC]: (text: React.ReactNode) => <em className="italic">{text}</em>,
    [MARKS.UNDERLINE]: (text: React.ReactNode) => <u className="underline">{text}</u>,
    [MARKS.CODE]: (text: React.ReactNode) => (
      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
        {text}
      </code>
    ),
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: unknown, children: React.ReactNode) => (
      <p className="mb-4 leading-relaxed text-gray-700">{children}</p>
    ),
    [BLOCKS.HEADING_1]: (node: unknown, children: React.ReactNode) => (
      <h1 className="text-4xl font-bold mb-6 text-gray-900">{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (node: unknown, children: React.ReactNode) => (
      <h2 className="text-3xl font-bold mb-5 text-gray-900">{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (node: unknown, children: React.ReactNode) => (
      <h3 className="text-2xl font-bold mb-4 text-gray-900">{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (node: unknown, children: React.ReactNode) => (
      <h4 className="text-xl font-bold mb-3 text-gray-900">{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (node: unknown, children: React.ReactNode) => (
      <h5 className="text-lg font-bold mb-3 text-gray-900">{children}</h5>
    ),
    [BLOCKS.HEADING_6]: (node: unknown, children: React.ReactNode) => (
      <h6 className="text-base font-bold mb-2 text-gray-900">{children}</h6>
    ),
    [BLOCKS.UL_LIST]: (node: unknown, children: React.ReactNode) => (
      <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (node: unknown, children: React.ReactNode) => (
      <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700">
        {children}
      </ol>
    ),
    [BLOCKS.LIST_ITEM]: (node: unknown, children: React.ReactNode) => (
      <li className="leading-relaxed">{children}</li>
    ),
    [BLOCKS.QUOTE]: (node: unknown, children: React.ReactNode) => (
      <blockquote className="border-l-4 border-blue-500 pl-6 py-4 mb-6 italic text-gray-700 bg-gray-50">
        {children}
      </blockquote>
    ),
    [BLOCKS.HR]: () => <hr className="my-8 border-gray-200" />,
    [BLOCKS.EMBEDDED_ASSET]: (node: unknown) => {
      const fields = (node as { data?: { target?: { fields?: { title?: string; description?: string; file?: { url?: string; contentType?: string } } } } })?.data?.target?.fields;
      if (!fields) return null;
      
      const { title, description, file } = fields;
      const imageUrl = file?.url?.startsWith('//') ? `https:${file.url}` : file?.url;
      
      if (file?.contentType?.startsWith('image/')) {
        return (
          <div className="my-8">
            <div className="w-full">
              <Image
                src={imageUrl || ''}
                alt={title || description || 'Content image'}
                width={800}
                height={256}
                className="w-full h-64 object-cover rounded-lg"
                loading="lazy"
              />
            </div>
            {(title || description) && (
              <p className="text-sm text-gray-600 text-center mt-2 italic">
                {title || description}
              </p>
            )}
          </div>
        );
      }
      
      // For other file types, show a download link
      return (
        <div className="my-4 p-4 border border-gray-200 rounded-lg">
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            📎 {title || 'Download file'}
          </a>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
      );
    },
    [INLINES.HYPERLINK]: (node: unknown, children: React.ReactNode) => {
      const { uri } = (node as { data: { uri: string } }).data;
      
      // Ensure uri is a string
      if (!uri || typeof uri !== 'string') {
        return <span>{children}</span>;
      }
      
      // Check if it's an internal link
      if (uri.startsWith('/') || (process.env.NEXT_PUBLIC_SITE_URL && uri.includes(process.env.NEXT_PUBLIC_SITE_URL))) {
        const href = uri.startsWith('/') ? uri : new URL(uri).pathname;
        const isCaseStudy = href.startsWith('/case-studies/');
        
        const link = (
          <Link href={href} className="text-blue-600 hover:text-blue-800 underline">
            {children}
          </Link>
        );

        // Wrap case study links with tooltip
        if (isCaseStudy) {
          return (
            <CursorTooltip
              tooltipText="Dive Deeper"
              backgroundColor="#AAFA96"
              textColor="#000000"
            >
              {link}
            </CursorTooltip>
          );
        }

        return link;
      }
      
      // External link
      return (
        <a
          href={uri}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {children}
        </a>
      );
    },
    [INLINES.ENTRY_HYPERLINK]: (node: unknown, children: React.ReactNode) => {
      // Handle links to other Contentful entries
      const target = (node as { data?: { target?: { sys?: { id?: string; contentType?: { sys?: { id?: string } } }; fields?: { slug?: string } } } })?.data?.target;
      if (!target) return <span>{children}</span>;
      
      const contentType = target.sys?.contentType?.sys?.id;
      
      // Generate appropriate link based on content type
      let href = '#';
      const isCaseStudy = contentType === 'caseStudy';
      if (isCaseStudy) {
        const slug = (target as { fields?: { slug?: string } }).fields?.slug;
        // Ensure slug is a string
        if (typeof slug === 'string' && slug.trim()) {
          href = `/case-studies/${slug}`;
        }
      } else if (contentType === 'page') {
        const slug = (target as { fields?: { slug?: string } }).fields?.slug;
        // Ensure slug is a string
        if (typeof slug === 'string' && slug.trim()) {
          href = `/${slug}`;
        }
      }
      
      const link = (
        <Link href={href} className="text-blue-600 hover:text-blue-800 underline">
          {children}
        </Link>
      );

      // Wrap case study links with tooltip
      if (isCaseStudy) {
        return (
          <CursorTooltip
            tooltipText="Dive Deeper"
            backgroundColor="#AAFA96"
            textColor="#000000"
          >
            {link}
          </CursorTooltip>
        );
      }
      
      return link;
    },
    [INLINES.ASSET_HYPERLINK]: (node: unknown, children: React.ReactNode) => {
      const fields = (node as { data?: { target?: { fields?: { file?: { url?: string } } } } })?.data?.target?.fields;
      if (!fields) return <span>{children}</span>;
      
      const { file } = fields;
      const assetUrl = file?.url?.startsWith('//') ? `https:${file.url}` : file?.url;
      
      return (
        <a
          href={typeof assetUrl === 'string' ? assetUrl : '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {children}
        </a>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: unknown) => {
      // Handle embedded entries (like call-to-action blocks, etc.)
      const entry = (node as { data?: { target?: { sys?: { contentType?: { sys?: { id?: string } } }; fields?: { title?: string; description?: string; buttonText?: string; buttonUrl?: string } } } })?.data?.target;
      if (!entry) return null;
      
      const contentType = entry.sys?.contentType?.sys?.id;
      const fields = (entry as { fields?: Record<string, unknown> }).fields;
      
      // You can add specific rendering for different content types
      if (contentType === 'callToAction' && fields) {
        return (
          <div className="my-8 p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <h3 className="text-xl font-bold mb-2">{String(fields.title || '')}</h3>
            {typeof fields.description === 'string' && fields.description && (
              <p className="text-gray-700 mb-4">{fields.description}</p>
            )}
            {typeof fields.buttonText === 'string' && fields.buttonText && typeof fields.buttonUrl === 'string' && fields.buttonUrl && (
              <Link
                href={fields.buttonUrl}
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition-colors"
              >
                {fields.buttonText}
              </Link>
            )}
          </div>
        );
      }
      
      // Default rendering for unknown embedded entries
      return (
        <div className="my-4 p-4 bg-gray-50 border rounded">
          <p className="text-sm text-gray-600">
            Embedded content: {contentType || 'Unknown type'}
          </p>
        </div>
      );
    },
    [BLOCKS.TABLE]: (node: unknown, children: React.ReactNode) => (
      <div className="my-6 overflow-x-auto">
        <table className="min-w-full border border-gray-200 rounded-lg">
          <tbody>{children}</tbody>
        </table>
      </div>
    ),
    [BLOCKS.TABLE_ROW]: (node: unknown, children: React.ReactNode) => (
      <tr className="border-b border-gray-200">{children}</tr>
    ),
    [BLOCKS.TABLE_CELL]: (node: unknown, children: React.ReactNode) => (
      <td className="px-4 py-2 border-r border-gray-200 last:border-r-0">
        {children}
      </td>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node: unknown, children: React.ReactNode) => (
      <th className="px-4 py-2 bg-gray-50 border-r border-gray-200 last:border-r-0 font-semibold text-left">
        {children}
      </th>
    ),
  },
};

export default function ContentRenderer({ content }: ContentRendererProps) {
  if (!content) {
    return null;
  }

  return (
    <div className="prose prose-lg max-w-none">
      {documentToReactComponents(content, renderOptions)}
    </div>
  );
}

